import React, { useState } from "react";

const ContactForm = () => {
  const [form, setForm] = useState({
    fullName: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });

  const [errors, setErrors] = useState({
    fullName: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  
  // Added state for form status messages
  const [formStatus, setFormStatus] = useState({
    message: "",
    isError: false,
    isSubmitting: false
  });

  const validateForm = () => {
    const newErrors = {
      fullName: "",
      email: "",
      phone: "",
      subject: "",
      message: "",
    };
    let isValid = true;

    // Full Name: Required, 2+ chars, letters/spaces only
    if (!form.fullName) {
      newErrors.fullName = "Full Name is required";
      isValid = false;
    } else if (!/^[a-zA-Z\s]{2,}$/.test(form.fullName)) {
      newErrors.fullName =
        "Full Name must be at least 2 characters (letters and spaces only)";
      isValid = false;
    }

    // Email: Required, valid format
    if (!form.email) {
      newErrors.email = "Email is required";
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      newErrors.email = "Please enter a valid email address";
      isValid = false;
    }

    // Phone: Required, valid format (+91 or 10-15 digits/spaces)
    if (!form.phone) {
      newErrors.phone = "Phone Number is required";
      isValid = false;
    } else if (!/^\+?[0-9\s]{10,15}$/.test(form.phone)) {
      newErrors.phone = "Phone Number must be 10-15 digits (optional +)";
      isValid = false;
    }

    // Subject: Optional, max 100 chars
    if (form.subject.length > 100) {
      newErrors.subject = "Subject must be 100 characters or less";
      isValid = false;
    }

    // Message: Optional, max 500 chars
    if (form.message.length > 500) {
      newErrors.message = "Message must be 500 characters or less";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    // Clear error for the field being edited
    setErrors({ ...errors, [e.target.name]: "" });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      // Show submitting status
      setFormStatus({
        message: "Submitting your message...",
        isError: false,
        isSubmitting: true
      });
      
      // Make sure the URL matches exactly where your PHP file is located
      fetch("https://bhagwatiengineering.co.in/form.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify(form),
      })
        .then((response) => {
          // Check if response is ok before parsing JSON
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          }
          return response.json();
        })
        .then((data) => {
          if (data.success) {
            setFormStatus({
              message: "Thank you! Your message has been sent.",
              isError: false,
              isSubmitting: false
            });
            setForm({
              fullName: "",
              email: "",
              phone: "",
              subject: "",
              message: "",
            });
          } else {
            setFormStatus({
              message: `Error: ${data.error || "Unknown error occurred"}`,
              isError: true,
              isSubmitting: false
            });
          }
        })
        .catch((error) => {
          console.error("Submission error:", error);
          setFormStatus({
            message: "Something went wrong while submitting the form. Please try again.",
            isError: true,
            isSubmitting: false
          });
        });
    }
  };

  return (
    <section id="contact" className="bg-white text-[#083e96] px-6">
      <div className="max-w-5xl mx-auto">
        <h2
          className="heading-contact-small"
          data-aos="fade-up"
          data-aos-delay="100"
        >
          Write to us
        </h2>

        {/* Form status messages */}
        {formStatus.message && (
          <div 
            className={`text-center p-3 mb-4 rounded ${
              formStatus.isError ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"
            }`}
          >
            {formStatus.message}
          </div>
        )}

        <form
          onSubmit={handleSubmit}
          className="grid grid-cols-1 md:grid-cols-2 gap-8 contact-form"
        >
          {/* Full Name */}
          <div data-aos="fade-up" data-aos-delay="200">
            <label className="block text-sm font-medium mb-1">Full Name*</label>
            <input
              type="text"
              name="fullName"
              placeholder="Name"
              value={form.fullName}
              onChange={handleChange}
              required
              className="w-full border-b-2 border-gray-300 focus:outline-none focus:border-[#083e96] py-1 text-[#083e96] font-semibold"
            />
            {errors.fullName && (
              <p className="text-red-500 text-xs mt-1">{errors.fullName}</p>
            )}
          </div>

          {/* Email Address */}
          <div data-aos="fade-up" data-aos-delay="300">
            <label className="block text-sm font-medium mb-1">
              Email Address*
            </label>
            <input
              type="email"
              name="email"
              placeholder="your@email.com"
              value={form.email}
              onChange={handleChange}
              required
              className="w-full border-b-2 border-gray-300 focus:outline-none focus:border-[#083e96] py-1 text-gray-500"
            />
            {errors.email && (
              <p className="text-red-500 text-xs mt-1">{errors.email}</p>
            )}
          </div>

          {/* Phone Number */}
          <div data-aos="fade-up" data-aos-delay="400">
            <label className="block text-sm font-medium mb-1">
              Phone Number*
            </label>
            <input
              type="tel"
              name="phone"
              placeholder="+91 1234567890"
              value={form.phone}
              onChange={handleChange}
              required
              className="w-full border-b-2 border-gray-300 focus:outline-none focus:border-[#083e96] py-1 text-gray-700"
            />
            {errors.phone ? (
              <p className="text-red-500 text-xs mt-1">{errors.phone}</p>
            ) : (
              <p className="text-xs text-gray-400 mt-1">
                Please enter a valid phone number.
              </p>
            )}
          </div>

          {/* Subject */}
          <div data-aos="fade-up" data-aos-delay="500">
            <label className="block text-sm font-medium mb-1">Subject</label>
            <input
              type="text"
              name="subject"
              placeholder="ex. General construction"
              value={form.subject}
              onChange={handleChange}
              className="w-full border-b-2 border-gray-300 focus:outline-none focus:border-[#083e96] py-1 text-gray-500"
            />
            {errors.subject && (
              <p className="text-red-500 text-xs mt-1">{errors.subject}</p>
            )}
          </div>

          {/* Message (full width) */}
          <div
            className="md:col-span-2"
            data-aos="fade-up"
            data-aos-delay="600"
          >
            <label className="block text-sm font-medium mb-1">Message</label>
            <textarea
              name="message"
              rows="2"
              placeholder="Write your message here...."
              value={form.message}
              onChange={handleChange}
              className="w-full border-b-2 border-gray-300 focus:outline-none focus:border-[#083e96] py-1 text-gray-500 resize-none"
            />
            {errors.message && (
              <p className="text-red-500 text-xs mt-1">{errors.message}</p>
            )}
          </div>

          {/* Submit Button (centered) */}
          <div
            className="md:col-span-2 text-center mt-4"
            data-aos="fade-up"
            data-aos-delay="700"
          >
            <button
              type="submit"
              className="bg-[#083e96] hover:bg-[#062c6d] text-white font-medium py-2 px-8 rounded-full"
              disabled={formStatus.isSubmitting}
            >
              {formStatus.isSubmitting ? "SUBMITTING..." : "SUBMIT"}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};

export default ContactForm;